import { useLayoutEffect, useRef } from 'react';
import { ArrowDown, ArrowUpRight, BadgeCheck, MapPin, Play } from 'lucide-react';
import gsap from 'gsap';
import { siteConfig, trustItems } from '../data/siteData.js';
import useReducedMotion from '../hooks/useReducedMotion.js';

export default function Hero() {
  const root = useRef(null);
  const reducedMotion = useReducedMotion();

  useLayoutEffect(() => {
    if (reducedMotion) return undefined;

    const context = gsap.context(() => {
      gsap
        .timeline({ defaults: { ease: 'power3.out' } })
        .from('.hero-kicker', { y: 22, opacity: 0, duration: 0.65 })
        .from('.hero-title-line', { yPercent: 105, duration: 0.9, stagger: 0.1 }, '-=0.3')
        .from('.hero-copy', { y: 26, opacity: 0, duration: 0.7 }, '-=0.45')
        .from('.hero-actions > *', { y: 18, opacity: 0, duration: 0.55, stagger: 0.09 }, '-=0.4')
        .from('.hero-trust-item', { y: 15, opacity: 0, duration: 0.5, stagger: 0.08 }, '-=0.35')
        .from('.hero-visual-card', { x: 50, opacity: 0, duration: 0.95 }, '-=0.8');

      gsap.to('.hero-orb--one', {
        x: 45,
        y: 28,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      gsap.to('.hero-orb--two', {
        x: -35,
        y: -24,
        duration: 10,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    }, root);

    return () => context.revert();
  }, [reducedMotion]);

  return (
    <section ref={root} className="hero" id="top">
      <div className="hero-orb hero-orb--one" aria-hidden="true" />
      <div className="hero-orb hero-orb--two" aria-hidden="true" />
      <div className="hero-grid" aria-hidden="true" />

      <div className="container hero-layout">
        <div className="hero-content">
          <div className="hero-kicker">
            <span className="status-dot" />
            {siteConfig.launchLabel}
          </div>

          <h1 className="hero-title" aria-label="Ne csak eddz. Építs olyan testet, amiben jól érzed magad.">
            <span className="hero-title-mask">
              <span className="hero-title-line">NE CSAK EDDZ.</span>
            </span>
            <span className="hero-title-mask">
              <span className="hero-title-line hero-title-line--accent">ÉPÍTSD FEL</span>
            </span>
            <span className="hero-title-mask">
              <span className="hero-title-line">ÖNMAGAD.</span>
            </span>
          </h1>

          <p className="hero-copy">
            Építs erősebb, egészségesebb és magabiztosabb önmagad.
            Személyi edzés Székesfehérváron a Cutlerben, valamint online coaching személyre szabott edzéstervvel és étrenddel.
          </p>

          <div className="hero-actions">
            <a className="button button--gold button--large" href="#kapcsolat">
              Ingyenes konzultáció
              <ArrowUpRight size={19} />
            </a>
            <a className="button button--ghost button--large" href="#szolgaltatasok">
              <Play size={18} fill="currentColor" />
              Megnézem, hogyan működik
            </a>
          </div>

          <div className="hero-location">
            <MapPin size={18} />
            <span>{siteConfig.location}</span>
          </div>

          <div className="hero-trust" aria-label="Fő előnyök">
            {trustItems.map(({ icon: Icon, text }) => (
              <div className="hero-trust-item" key={text}>
                <Icon size={17} />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-visual" aria-label="Személyi edzői bemutatkozó kép">
          <div className="hero-visual-frame">
            <img
              src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?auto=format&fit=crop&w=1400&q=90"
              alt="Edzés súlyzókkal egy modern konditeremben"
            />
            <div className="hero-visual-shade" />
            <div className="hero-vertical-label">DISCIPLINE • STRENGTH • PROGRESS</div>
          </div>

          <div className="hero-visual-card glass-card">
            <div className="hero-card-icon">
              <BadgeCheck size={22} />
            </div>
            <div>
              <small>Első lépés</small>
              <strong>Ingyenes célfelmérés</strong>
              <span>Kötöttségek nélkül</span>
            </div>
          </div>

          <div className="hero-number" aria-hidden="true">01</div>
        </div>
      </div>

      <a className="scroll-indicator" href="#statisztikak" aria-label="Görgetés a következő szakaszhoz">
        <span>Görgess</span>
        <ArrowDown size={17} />
      </a>
    </section>
  );
}
